import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import FloatingAIChat from '../../components/ui/FloatingAIChat';
import HeroSection from './components/HeroSection';
import ContentRail from './components/ContentRail';
import QuickAccessSection from './components/QuickAccessSection';
import StatsOverview from './components/StatsOverview';
import RecentActivity from './components/RecentActivity';
import { useCourseAndModules } from '../../hooks/useCourseData';
import { DEFAULT_COURSE_SLUG } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useUserProgress } from '../../hooks/useUserProgress';

const DashboardHome = () => {
  const [isLoading, setIsLoading] = useState(true);
  const { user, session } = useAuth();
  
  // Hook otimizado para carregar cursos e módulos com courseSlug específico
  const { course, modules, loading: supabaseLoading, error } = useCourseAndModules(DEFAULT_COURSE_SLUG);
  const { progress, loading: progressLoading } = useUserProgress(
    session?.systemUser?.id,
    user?.id,
    course?.id ? String(course.id) : undefined
  );
  
  // Transform modules data for ContentRail
  const transformedModules = modules?.map(module => ({
    id: module.id,
    title: module.title,
    description: module.summary || '',
    thumbnail: module.cover || '/assets/images/no_image.png',
    duration: '7 lições',
    progress: progress?.moduleProgress?.[String(module.id)] ?? 0,
    instructor: 'Diane Andrade',
    status: module.status === 'coming_soon' ? 'coming-soon' : 'available',
    type: 'course',
    lessons: module.lessons?.length ?? 0,
    moduleSlug: module.slug
  })) || [];

  // Empty state for content rails - será populado do Supabase
  const continueWatchingData = [];
  const recommendedData = [];
  const newReleasesData = [];
  const devotionalData = [];

  useEffect(() => {
    // Aguardar dados do Supabase carregarem
    if (!supabaseLoading && !progressLoading) {
      setIsLoading(false);
    }
  }, [supabaseLoading, progressLoading]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Modulo 1 - Academy Platform</title>
        </Helmet>
        <Header />
        <main className="pt-16">
          <div className="container mx-auto px-6 py-8">
            {/* Hero Skeleton */}
            <div className="h-[500px] md:h-[600px] bg-muted rounded-lg mb-8 animate-pulse"></div>
            
            {/* Stats Skeleton */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
              {[...Array(4)]?.map((_, i) => (
                <div key={i} className="h-24 bg-muted rounded-lg animate-pulse"></div>
              ))}
            </div>

            {/* Rails Skeleton */}
            {[...Array(3)]?.map((_, i) => (
              <div key={i} className="mb-12">
                <div className="h-8 bg-muted rounded w-64 mb-6 animate-pulse"></div>
                <div className="flex space-x-4">
                  {[...Array(4)]?.map((_, j) => (
                    <div key={j} className="flex-shrink-0 w-72 h-48 bg-muted rounded-lg animate-pulse"></div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Início - Academy Platform</title>
        <meta name="description" content="Sua plataforma de aprendizado cristão com cursos, atividades e mentoria IA." />
      </Helmet>

      <Header />
      
      <main className="pt-16">
        <div className="container mx-auto px-6 py-8">
          {/* Hero Section */}
          <HeroSection course={course} progress={progress} />

          {/* Stats Overview */}
          <StatsOverview progress={progress} />

          {/* Continue Watching Rail */}
          {/* TODO: WORKING ON THIS - <ContentRail
            title="Continue Assistindo"
            items={continueWatchingData}
            showProgress={true}
            emptyMessage="Você não tem cursos em andamento"
          /> */}

          {/* Quick Access Section */}
          <QuickAccessSection />

          {/* Modules Rail - Dados do Supabase */}
          <ContentRail
            title="Módulos do Curso"
            items={transformedModules}
            emptyMessage="Nenhum módulo disponível no momento"
          />

          {/* Recommended Rail */}
          <ContentRail
            title="Recomendado para Você"
            items={recommendedData}
            emptyMessage="Nenhuma recomendação disponível no momento"
          />

          {/* TODO: WORKING ON THIS - Recent Activity */}
          {/* <RecentActivity
           title="Atividade Recente"
           emptyMessage="Nenhuma atividade recente disponível no momento"
          /> */}

          {/* New Releases Rail */}
          {/* TODO: WORKING ON THIS - <ContentRail
            title="Novos Lançamentos"
            items={newReleasesData}
            emptyMessage="Nenhum lançamento recente"
          /> */}

          {/* Devotional Content Rail */}
          <ContentRail
            title="Conteúdo Devocional"
            items={devotionalData}
            emptyMessage="Nenhum conteúdo devocional disponível"
          />
        </div>
      </main>

      {/* Floating AI Chat */}
       <FloatingAIChat />
    </div>
  );
};

export default DashboardHome;
